import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import type { TFunction } from 'i18next';

export class EmptyFileError extends Error {
  readonly fileName: string;

  constructor(fileName: string) {
    super('emptyFileSelected');
    this.name = 'EmptyFileError';
    this.fileName = fileName;
  }
}

export class FileReadError extends Error {
  readonly fileName: string;

  constructor(fileName: string) {
    super('fileReadFailed');
    this.name = 'FileReadError';
    this.fileName = fileName;
  }
}

export function translateFileReadError(error: unknown, t: TFunction): string {
  if (error instanceof EmptyFileError) {
    return t('emptyFileSelected', { name: error.fileName });
  }
  if (error instanceof FileReadError) {
    return t('fileReadFailed', { name: error.fileName });
  }
  return t('fileReadFailed', { name: '' });
}

export class FileReaderHelper {
  static arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  static async fileToDocumentDtoOut(file: File, description?: string): Promise<DocumentDtoOut[]> {
    if (file.size === 0) {
      throw new EmptyFileError(file.name);
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (progressEvent) => {
        const result = (progressEvent.target as FileReader | null)?.result;
        if (!(result instanceof ArrayBuffer) || result.byteLength === 0) {
          reject(new EmptyFileError(file.name));
          return;
        }
        const base64 = this.arrayBufferToBase64(result);
        if (!base64) {
          reject(new FileReadError(file.name));
          return;
        }
        resolve([{
          name: file.name,
          description: description || file.name,
          contentBase64: base64,
        }]);
      };
      reader.onerror = () => {
        reject(new FileReadError(file.name));
      };
      reader.readAsArrayBuffer(file);
    });
  }
}
